import api from './api';

export interface Company {
  id: string;
  name: string;
  industry?: string;
  sector?: string;
  size_category?: string;
  revenue_range?: string;
  employee_count?: number;
  headquarters_location?: string;
  website?: string;
  description?: string;
  pe_owner_id?: string;
  pe_owner_name?: string;
  acquisition_date?: string;
  acquisition_value?: number;
  status: 'active' | 'inactive' | 'do_not_contact' | 'prospect';
  created_at: string;
  updated_at?: string;
  created_by: string;
  created_by_name?: string;
}

export interface CreateCompanyRequest {
  name: string;
  industry?: string;
  sector?: string;
  size_category?: string;
  revenue_range?: string;
  employee_count?: number;
  headquarters_location?: string;
  website?: string;
  description?: string;
  pe_owner_id?: string;
  acquisition_date?: string;
  acquisition_value?: number;
  status?: 'active' | 'inactive' | 'do_not_contact' | 'prospect';
}

export interface UpdateCompanyRequest {
  name?: string;
  industry?: string;
  sector?: string;
  size_category?: string;
  revenue_range?: string;
  employee_count?: number;
  headquarters_location?: string;
  website?: string;
  description?: string;
  pe_owner_id?: string;
  acquisition_date?: string;
  acquisition_value?: number;
  status?: 'active' | 'inactive' | 'do_not_contact' | 'prospect';
}

export interface CompanyFilters {
  name?: string;
  industry?: string;
  sector?: string;
  size_category?: string;
  status?: string;
  pe_owner_id?: string;
  created_by?: string;
  limit?: number;
  offset?: number;
}

export interface Contact {
  id: string;
  company_id: string;
  company_name?: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  title?: string;
  job_title?: string;
  department?: string;
  linkedin_url?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  lead_source?: string;
  lead_score?: number;
  status: 'active' | 'inactive' | 'do_not_contact' | 'prospect';
  notes?: string;
  follow_up_date?: string;
  created_at: string;
  updated_at?: string;
  created_by: string;
  created_by_name?: string;
}

export interface CreateContactRequest {
  company_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  title?: string;
  job_title?: string;
  department?: string;
  linkedin_url?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  lead_source?: string;
  lead_score?: number;
  status?: 'active' | 'inactive' | 'do_not_contact' | 'prospect';
  notes?: string;
  follow_up_date?: string;
}

export interface UpdateContactRequest {
  company_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  title?: string;
  job_title?: string;
  department?: string;
  linkedin_url?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  lead_source?: string;
  lead_score?: number;
  status?: 'active' | 'inactive' | 'do_not_contact' | 'prospect';
  notes?: string;
  follow_up_date?: string;
}

export interface ContactFilters {
  company_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  job_title?: string;
  department?: string;
  status?: string;
  lead_source?: string;
  created_by?: string;
  email_opt_out?: boolean;
  limit?: number;
  offset?: number;
}

export interface Interaction {
  id: string;
  contact_id: string;
  contact_name?: string;
  company_id: string;
  company_name?: string;
  interaction_type: 'email' | 'call' | 'meeting' | 'demo' | 'proposal' | 'presentation' | 'follow_up' | 'phone';
  subject?: string;
  description?: string;
  outcome?: string;
  duration_minutes?: number;
  interaction_date?: string;
  next_action?: string;
  next_action_date?: string;
  follow_up_date?: string;
  created_at: string;
  updated_at?: string;
  created_by: string;
  created_by_name?: string;
}

export interface CreateInteractionRequest {
  contact_id: string;
  company_id: string;
  interaction_type: 'email' | 'call' | 'meeting' | 'demo' | 'proposal' | 'presentation' | 'follow_up' | 'phone';
  subject?: string;
  description?: string;
  outcome?: string;
  duration_minutes?: number;
  interaction_date?: string;
  next_action?: string;
  next_action_date?: string;
  follow_up_date?: string;
}

export interface UpdateInteractionRequest {
  contact_id?: string;
  company_id?: string;
  interaction_type?: 'email' | 'call' | 'meeting' | 'demo' | 'proposal' | 'presentation' | 'follow_up' | 'phone';
  subject?: string;
  description?: string;
  outcome?: string;
  duration_minutes?: number;
  interaction_date?: string;
  next_action?: string;
  next_action_date?: string;
  follow_up_date?: string;
}

export interface InteractionFilters {
  contact_id?: string;
  company_id?: string;
  interaction_type?: string;
  outcome?: string;
  created_by?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  template_id?: string;
  template_name?: string;
  email_content?: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'cancelled' | 'failed';
  scheduled_date?: string;
  scheduled_timezone?: string;
  sent_date?: string;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  bounced_count: number;
  unsubscribed_count: number;
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
  created_at: string;
  updated_at?: string;
  created_by: string;
  created_by_name?: string;
}

export interface CreateEmailCampaignRequest {
  name: string;
  subject: string;
  template_id?: string;
  email_content?: string;
  status?: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'cancelled' | 'failed';
  scheduled_date?: string;
}

export interface UpdateEmailCampaignRequest {
  name?: string;
  subject?: string;
  template_id?: string;
  email_content?: string;
  status?: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'cancelled' | 'failed';
  scheduled_date?: string;
  total_recipients?: number;
}

export interface EmailCampaignFilters {
  name?: string;
  status?: string;
  created_by?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  content_html: string;
  content_text: string;
  category?: string;
  is_active: boolean;
  created_at: string;
  created_by: string;
  created_by_name?: string;
}

export interface CreateEmailTemplateRequest {
  name: string;
  subject: string;
  content_html: string;
  content_text: string;
  category?: string;
  is_active?: boolean;
}

export interface UpdateEmailTemplateRequest {
  name?: string;
  subject?: string;
  email_content?: string;
  category?: string;
  is_active?: boolean;
}

export interface CampaignRecipient {
  id: string;
  campaign_id: string;
  contact_id: string | null;
  email_address?: string;
  recipient_type?: 'to' | 'cc' | 'bcc';
  contact_name?: string;
  contact_email?: string;
  company_name?: string;
  status: 'pending' | 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'unsubscribed';
  sent_at?: string;
  delivered_at?: string;
  opened_at?: string;
  clicked_at?: string;
  bounced_at?: string;
  unsubscribed_at?: string;
  open_count: number;
  click_count: number;
  last_clicked_link?: string;
  created_at: string;
}

export interface CampaignAnalytics {
  campaign_id: string;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  bounced_count: number;
  unsubscribed_count: number;
  open_rate: number;
  click_rate: number;
  bounce_rate: number;
  unsubscribe_rate: number;
  unique_opens: number;
  unique_clicks: number;
  total_opens: number;
  total_clicks: number;
  top_clicked_links: Array<{
    url: string;
    click_count: number;
  }>;
  opens_by_hour: Array<{
    hour: number;
    count: number;
  }>;
  clicks_by_hour: Array<{
    hour: number;
    count: number;
  }>;
}

class CRMService {
  // Company methods
  async getCompanies(filters: CompanyFilters = {}): Promise<any> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/crm/companies${queryString ? `?${queryString}` : ''}`);
    return response.data; // Return the full response object with total, totalPages, etc.
  }

  async getCompanyStats(): Promise<{ total: number; by_status: Record<string, number>; by_industry: Record<string, number>; by_size: Record<string, number> }> {
    const response = await api.get('/crm/companies/stats');
    return response.data.data || response.data;
  }

  // FlexibleCRUDTable compatibility methods
  async getAll(filters: any = {}): Promise<Company[]> {
    return this.getCompanies(filters);
  }

  async get(id: string): Promise<Company> {
    return this.getCompanyById(id);
  }

  async create(data: CreateCompanyRequest): Promise<Company> {
    return this.createCompany(data);
  }

  async update(id: string, data: UpdateCompanyRequest): Promise<Company> {
    return this.updateCompany(id, data);
  }

  async delete(id: string): Promise<void> {
    return this.deleteCompany(id);
  }

  async getCompanyById(id: string): Promise<Company> {
    const response = await api.get(`/crm/companies/${id}`);
    return response.data.data || response.data;
  }

  async createCompany(companyData: CreateCompanyRequest): Promise<Company> {
    const response = await api.post('/crm/companies', companyData);
    return response.data.data || response.data;
  }

  async updateCompany(id: string, companyData: UpdateCompanyRequest): Promise<Company> {
    const response = await api.put(`/crm/companies/${id}`, companyData);
    return response.data.data || response.data;
  }

  async deleteCompany(id: string): Promise<void> {
    await api.delete(`/crm/companies/${id}`);
  }

  async searchCompanies(query: string, limit: number = 10): Promise<Company[]> {
    const response = await api.get(`/crm/companies/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data.data || response.data;
  }


  // Contact methods
  async getContacts(filters: ContactFilters = {}): Promise<any> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/crm/contacts${queryString ? `?${queryString}` : ''}`);
    return response.data; // Return the full response object with total, totalPages, etc.
  }

  async getContactById(id: string): Promise<Contact> {
    const response = await api.get(`/crm/contacts/${id}`);
    return response.data.data || response.data;
  }

  async createContact(contactData: CreateContactRequest): Promise<Contact> {
    const response = await api.post('/crm/contacts', contactData);
    return response.data.data || response.data;
  }

  async updateContact(id: string, contactData: UpdateContactRequest): Promise<Contact> {
    const response = await api.put(`/crm/contacts/${id}`, contactData);
    return response.data.data || response.data;
  }

  async deleteContact(id: string): Promise<void> {
    await api.delete(`/crm/contacts/${id}`);
  }

  async searchContacts(query: string, limit: number = 10): Promise<Contact[]> {
    const response = await api.get(`/crm/contacts/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data.data || response.data;
  }

  async getContactStats(): Promise<any> {
    const response = await api.get('/crm/contacts/stats');
    return response.data.data || response.data;
  }

  // Interaction methods
  async getInteractions(filters: InteractionFilters = {}): Promise<any> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/crm/interactions${queryString ? `?${queryString}` : ''}`);
    return response.data; // Return the full response object with total, totalPages, etc.
  }

  async getInteractionById(id: string): Promise<Interaction> {
    const response = await api.get(`/crm/interactions/${id}`);
    return response.data.data || response.data;
  }

  async createInteraction(interactionData: CreateInteractionRequest): Promise<Interaction> {
    const response = await api.post('/crm/interactions', interactionData);
    return response.data.data || response.data;
  }

  async updateInteraction(id: string, interactionData: UpdateInteractionRequest): Promise<Interaction> {
    const response = await api.put(`/crm/interactions/${id}`, interactionData);
    return response.data.data || response.data;
  }

  async deleteInteraction(id: string): Promise<void> {
    try {
      const response = await api.delete(`/crm/interactions/${id}`);
      
      // Verify the response indicates success
      if (!response.data || (response.data.success === false)) {
        const errorMsg = response.data?.message || 'Delete failed';
        throw new Error(errorMsg);
      }
      
      // Check HTTP status code
      if (response.status < 200 || response.status >= 300) {
        const errorMsg = `Delete failed with status ${response.status}`;
        throw new Error(errorMsg);
      }
      
      return response.data;
    } catch (error: any) {
      // Re-throw with a user-friendly message
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete interaction';
      throw new Error(errorMessage);
    }
  }

  async getInteractionStats(): Promise<any> {
    const response = await api.get('/crm/interactions/stats');
    return response.data.data || response.data;
  }

  // Email Campaign methods
  async getEmailCampaigns(filters: EmailCampaignFilters = {}): Promise<EmailCampaign[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/crm/campaigns${queryString ? `?${queryString}` : ''}`);
    return response.data.data || response.data;
  }

  async getEmailCampaignById(id: string): Promise<EmailCampaign> {
    const response = await api.get(`/crm/campaigns/${id}`);
    return response.data.data || response.data;
  }

  async createEmailCampaign(campaignData: CreateEmailCampaignRequest): Promise<EmailCampaign> {
    const response = await api.post('/crm/campaigns', campaignData);
    return response.data.data || response.data;
  }

  async updateEmailCampaign(id: string, campaignData: UpdateEmailCampaignRequest): Promise<EmailCampaign> {
    const response = await api.put(`/crm/campaigns/${id}`, campaignData);
    return response.data.data || response.data;
  }

  async deleteEmailCampaign(id: string): Promise<void> {
    await api.delete(`/crm/campaigns/${id}`);
  }

  async sendEmailCampaign(id: string): Promise<{ sent: number; failed: number; errors: string[]; status: string }> {
    const response = await api.post(`/crm/campaigns/${id}/send`);
    return response.data.data || response.data;
  }

  async getCampaignRecipients(campaignId: string): Promise<CampaignRecipient[]> {
    const response = await api.get(`/crm/campaigns/${campaignId}/recipients`);
    return response.data.data || response.data;
  }

  async getCampaignAnalytics(campaignId: string): Promise<CampaignAnalytics> {
    const response = await api.get(`/crm/campaigns/${campaignId}/analytics`);
    return response.data.data || response.data;
  }

  // Email Template methods
  async getEmailTemplates(): Promise<EmailTemplate[]> {
    const response = await api.get('/crm/email-templates');
    return response.data.data || response.data;
  }

  async getEmailTemplateById(id: string): Promise<EmailTemplate> {
    const response = await api.get(`/crm/email-templates/${id}`);
    return response.data.data || response.data;
  }

  async createEmailTemplate(templateData: CreateEmailTemplateRequest): Promise<EmailTemplate> {
    const response = await api.post('/crm/email-templates', templateData);
    return response.data.data || response.data;
  }

  async updateEmailTemplate(id: string, templateData: UpdateEmailTemplateRequest): Promise<EmailTemplate> {
    const response = await api.put(`/crm/email-templates/${id}`, templateData);
    return response.data.data || response.data;
  }

  async deleteEmailTemplate(id: string): Promise<void> {
    await api.delete(`/crm/email-templates/${id}`);
  }
}

export const crmService = new CRMService();

// Contact service for FlexibleCRUDTable compatibility
class ContactService {
  private crmService: CRMService;

  constructor(crmService: CRMService) {
    this.crmService = crmService;
  }

  async getAll(filters: any = {}): Promise<Contact[]> {
    return this.crmService.getContacts(filters);
  }

  async get(id: string): Promise<Contact> {
    return this.crmService.getContactById(id);
  }

  async create(data: CreateContactRequest): Promise<Contact> {
    return this.crmService.createContact(data);
  }

  async update(id: string, data: UpdateContactRequest): Promise<Contact> {
    return this.crmService.updateContact(id, data);
  }

  async delete(id: string): Promise<void> {
    return this.crmService.deleteContact(id);
  }
}

export const contactService = new ContactService(crmService);

// Interaction service for FlexibleCRUDTable compatibility
class InteractionService {
  private crmService: CRMService;

  constructor(crmService: CRMService) {
    this.crmService = crmService;
  }

  async getAll(filters: any = {}): Promise<Interaction[]> {
    return this.crmService.getInteractions(filters);
  }

  async get(id: string): Promise<Interaction> {
    return this.crmService.getInteractionById(id);
  }

  async create(data: CreateInteractionRequest): Promise<Interaction> {
    return this.crmService.createInteraction(data);
  }

  async update(id: string, data: UpdateInteractionRequest): Promise<Interaction> {
    return this.crmService.updateInteraction(id, data);
  }

  async delete(id: string): Promise<void> {
    return this.crmService.deleteInteraction(id);
  }
}

export const interactionService = new InteractionService(crmService);

// Email Campaign service for FlexibleCRUDTable compatibility
class EmailCampaignService {
  private crmService: CRMService;

  constructor(crmService: CRMService) {
    this.crmService = crmService;
  }

  async getAll(filters: any = {}): Promise<EmailCampaign[]> {
    return this.crmService.getEmailCampaigns(filters);
  }

  async get(id: string): Promise<EmailCampaign> {
    return this.crmService.getEmailCampaignById(id);
  }

  async create(data: CreateEmailCampaignRequest): Promise<EmailCampaign> {
    return this.crmService.createEmailCampaign(data);
  }

  async update(id: string, data: UpdateEmailCampaignRequest): Promise<EmailCampaign> {
    return this.crmService.updateEmailCampaign(id, data);
  }

  async delete(id: string): Promise<void> {
    return this.crmService.deleteEmailCampaign(id);
  }
}

export const emailCampaignService = new EmailCampaignService(crmService);

// Email Template service for FlexibleCRUDTable compatibility
class EmailTemplateService {
  private crmService: CRMService;

  constructor(crmService: CRMService) {
    this.crmService = crmService;
  }

  async getAll(): Promise<EmailTemplate[]> {
    return this.crmService.getEmailTemplates();
  }

  async get(id: string): Promise<EmailTemplate> {
    return this.crmService.getEmailTemplateById(id);
  }

  async create(data: CreateEmailTemplateRequest): Promise<EmailTemplate> {
    return this.crmService.createEmailTemplate(data);
  }

  async update(id: string, data: UpdateEmailTemplateRequest): Promise<EmailTemplate> {
    return this.crmService.updateEmailTemplate(id, data);
  }

  async delete(id: string): Promise<void> {
    return this.crmService.deleteEmailTemplate(id);
  }
}

export const emailTemplateService = new EmailTemplateService(crmService);
